import axios from 'axios';
import { setBearer } from './authRefresh';

/**
 * In-session MFA step-up (issue #854).
 *
 * A trusted-device sign-in (#564) gives a session that every admin route — and
 * the forms and Wiki MFA checks — refuses with
 * `403 "Multi-factor authentication required"` until the session carries the
 * code factor. This module turns that 403 into one "Confirm it's you" prompt
 * for the whole app: the first matching 403 asks the dialog to open, every
 * other 403 that arrives while it is open waits on the same prompt, and after
 * a good code each waiting request is sent again once with the new token.
 *
 * Re-sending is safe: every MFA check runs before the route writes anything,
 * so a refused request did nothing.
 *
 * The dialog (`components/auth/StepUpDialog.jsx`) registers itself as the
 * host. With no host mounted a prompt is refused at once instead of leaving a
 * request waiting forever.
 */

/**
 * The 403 `detail` every MFA gate returns. Matched exactly, on purpose: the
 * backend keeps it a plain string because ~200 call sites render `detail`
 * straight into a toast, and `X-Error-Code` is not readable cross-origin.
 * Rewording it on the server silently turns this prompt off.
 */
export const MFA_REQUIRED_DETAIL = 'Multi-factor authentication required';

/** Shared copy for pages that show a blocked state after a cancelled prompt. */
export const STEP_UP_NOTICE = 'Admin pages need your authenticator code on this device.';
export const STEP_UP_ACTION_LABEL = 'Enter code';
export const STEP_UP_VIEW_AS_NOTICE = "Admin pages aren't available while viewing as another user.";
export const STEP_UP_UNAVAILABLE_NOTICE = 'Sign out, then sign in again with your authenticator code to use admin pages.';

export function isStepUpRequired(err) {
    return err?.response?.status === 403 && err?.response?.data?.detail === MFA_REQUIRED_DETAIL;
}

export class StepUpCancelled extends Error {
    constructor() {
        super('Step-up cancelled');
        this.name = 'StepUpCancelled';
    }
}

let host = null;
let pending = null;
// After a cancel, a 403 does not re-open the prompt on its own until the user
// asks again ("Enter code") or moves to another page (the dialog calls
// allowStepUpPrompt on navigation). Otherwise a page's parallel fetches, or a
// poll, would bounce the prompt straight back.
let suppressed = false;
// The access token the server said can never step up ("view as", SSO). Tied
// to the token so a later sign-in in the same tab is asked again.
let unavailableToken = null;

function isUnavailable() {
    const token = localStorage.getItem('crm_token');
    return Boolean(token) && token === unavailableToken;
}

/**
 * Register the dialog that shows the prompt. Returns the unregister function,
 * which also cancels a prompt still open — nobody could answer it.
 */
export function registerStepUpHost(open) {
    host = open;
    return () => {
        if (host === open) {
            host = null;
            cancelStepUp();
        }
    };
}

/**
 * Ask for the code once. Resolves with the new access token, rejects with
 * StepUpCancelled. Concurrent callers share the same prompt.
 */
export function requestStepUp() {
    if (pending) return pending.promise;
    if (!host || suppressed || isUnavailable()) {
        return Promise.reject(new StepUpCancelled());
    }
    let resolve;
    let reject;
    const promise = new Promise((res, rej) => {
        resolve = res;
        reject = rej;
    });
    pending = { promise, resolve, reject };
    host();
    return promise;
}

export function resolveStepUp(accessToken) {
    if (!pending) return;
    const { resolve } = pending;
    pending = null;
    resolve(accessToken);
}

export function cancelStepUp() {
    if (!pending) return;
    const { reject } = pending;
    pending = null;
    suppressed = true;
    reject(new StepUpCancelled());
}

/** Let the next MFA 403 open the prompt again (navigation, "Enter code"). */
export function allowStepUpPrompt() {
    suppressed = false;
}

/** The server refused this session outright (`step_up_unavailable`). */
export function markStepUpUnavailable() {
    unavailableToken = localStorage.getItem('crm_token');
    cancelStepUp();
}

/**
 * What a page blocked by the MFA 403 should say, and whether an "Enter code"
 * button can help: never while viewing as someone, nor for a session the
 * server refused.
 */
export function stepUpBlockedNotice() {
    if (localStorage.getItem('crm_impersonating')) {
        return { text: STEP_UP_VIEW_AS_NOTICE, actionable: false };
    }
    if (isUnavailable()) {
        return { text: STEP_UP_UNAVAILABLE_NOTICE, actionable: false };
    }
    return { text: STEP_UP_NOTICE, actionable: true };
}

/**
 * An explicit "Enter code" click: ask now, then reload the page so every panel
 * refetches with the new token (a single retry would leave the panels that
 * loaded empty behind).
 */
export function stepUpThenReload(reload = () => window.location.reload()) {
    allowStepUpPrompt();
    return requestStepUp().then(() => reload(), () => {});
}

function isAuthPath(url) {
    return /\/api\/auth\//.test(String(url || '').split(/[?#]/)[0]);
}

function bearerOf(config) {
    const headers = config.headers;
    if (!headers) return undefined;
    if (typeof headers.get === 'function') return headers.get('Authorization');
    return headers.Authorization || headers.authorization;
}

let interceptorInstalled = false;

/**
 * Global axios response interceptor for the MFA 403. Installed right after the
 * 401 refresh interceptor (`lib/authRefresh.js`).
 *
 * A request can opt out with `{ skipStepUp: true }`: background work (e.g. the
 * sidebar's admin badge poll) must never open the prompt on its own.
 */
export function installStepUpInterceptor() {
    if (interceptorInstalled) return;
    interceptorInstalled = true;

    axios.interceptors.response.use(
        (response) => response,
        async (error) => {
            const { config } = error;
            if (!config || !isStepUpRequired(error)) return Promise.reject(error);
            // Sent once already with the new token: the refusal is final.
            if (config._stepUpRetry) return Promise.reject(error);
            if (config.skipStepUp) return Promise.reject(error);
            // Auth endpoints own their errors; the step-up call itself lives there.
            if (isAuthPath(config.url)) return Promise.reject(error);
            // Signed out, or "view as" — never ask an admin for someone else's code.
            const stored = localStorage.getItem('crm_token');
            if (!stored || localStorage.getItem('crm_impersonating')) {
                return Promise.reject(error);
            }
            // Sent with an older token than the one now stored — a request in
            // flight during a step-up, another tab, or a stale header closure.
            // Try the stored token before asking for a code again.
            if (!config._stepUpTokenSwap && bearerOf(config) !== `Bearer ${stored}`) {
                config._stepUpTokenSwap = true;
                setBearer(config, stored);
                return axios(config);
            }

            let token;
            try {
                token = await requestStepUp();
            } catch {
                return Promise.reject(error);
            }
            config._stepUpRetry = true;
            setBearer(config, token);
            return axios(config);
        }
    );
}
